// __ROUTE_DRAFTS__ (2026-10-03) Pure helpers for the Route Builder: tile text,
// selection, the stop-order optimiser and the finalize plan. No React, no
// Supabase - sim/routebuilder.test.mjs runs every function here.
import { isBillableStop } from './stopKinds';

// ---------------------------------------------------------------- tile text
const PROVINCES = 'ON|QC|BC|AB|MB|SK|NS|NB|NL|PE|YT|NT|NU|ON\\.';

// "123 Main St, Guelph, ON N1H 1A1, Canada" -> "123 Main St, Guelph"
export const shortAddress = (address) => {
  const raw = String(address || '').trim();
  if (!raw) return '';
  let parts = raw.split(',').map((p) => p.trim()).filter(Boolean);
  parts = parts.filter((p) => !/^(canada|usa|united states|us)$/i.test(p));
  // drop a trailing "ON N1H 1A1" / "ON" / postal-code-only part
  const provRe = new RegExp('^(' + PROVINCES + ')(\\s+[A-Z]\\d[A-Z]\\s?\\d[A-Z]\\d)?$', 'i');
  while (parts.length > 1 && (provRe.test(parts[parts.length - 1]) || /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i.test(parts[parts.length - 1]))) parts.pop();
  return parts.slice(0, 2).join(', ');
};

const stopRank = (s) => {
  const t = String((s && s.stop_type) || '').toLowerCase();
  return t === 'pickup' ? 0 : t === 'delivery' ? 1 : 2;
};

// The board's stop order for one job (same as the route engine): pickups,
// then drop-offs, then anything else, each by stop_sequence.
export const orderedStops = (load) => [...((load && load.load_stops) || [])]
  .sort((a, b) => stopRank(a) - stopRank(b) || (Number(a.stop_sequence) || 0) - (Number(b.stop_sequence) || 0));

// A stop's name on a tile: the business name when the hub found one, else the address.
export const stopLabel = (stop) => {
  const name = String((stop && stop.place_name) || '').trim();
  if (name) return name;
  return shortAddress(stop && stop.address) || '(no address)';
};

// { line: 'Pickup Co -> Drop Co (+2 stops)', stops: [{kind, label, address}], tooltip }
export const loadRouteSummary = (load) => {
  const all = orderedStops(load);
  const customer = all.filter(isBillableStop);
  const shown = customer.length ? customer : all;
  const stops = all.map((s) => ({
    kind: String(s.stop_type || 'stop').toLowerCase(),
    label: stopLabel(s),
    address: String(s.address || '').trim(),
    driverAdded: !isBillableStop(s),
  }));
  let line;
  if (shown.length === 0) line = 'No stops yet';
  else if (shown.length === 1) line = stopLabel(shown[0]);
  else {
    line = stopLabel(shown[0]) + ' → ' + stopLabel(shown[shown.length - 1]);
    if (shown.length > 2) line = stopLabel(shown[0]) + ' → ' + shown.slice(1).map(stopLabel).join(' → ');
  }
  const kindWord = (k) => (k === 'pickup' ? 'Pickup' : k === 'delivery' ? 'Drop-off' : k.charAt(0).toUpperCase() + k.slice(1));
  const tooltip = stops.map((s, i) => (i + 1) + '. ' + kindWord(s.kind) + (s.driverAdded ? ' (driver-added)' : '') + ': '
    + s.label + (s.address && s.address !== s.label ? ' - ' + s.address : '')).join('\n') || 'No stops yet';
  return { line, stops, tooltip };
};

// ---------------------------------------------------------------- card face
// __CARD_FACE__ (2026-10-16, Henry: "Similar to Trello ... the jobs should display the from and to address as well
// as customer at face view, not the load number. The load number holds no value for glance view.")
// The face of every job card / tile: the customer, then FROM -> TO the way the hub's Trello titles read
// (business / address-book name first, else street + town). The load number lives in the opened job.
export const customerLabel = (load) =>
  String((load && ((load.customer && load.customer.company_name) || load.customer_name)) || '').trim();

// 'Highpoint Metals → Hopeway Laser'; 3+ stops: 'From → … +N stops → To' (customer stops; driver-added ones are not
// part of the job's face).
// The same in parts, so a narrow card can keep the To on its own line: { from, mid: ' → … +N stops' | '', to }.
export const faceParts = (load) => {
  const all = orderedStops(load);
  const customer = all.filter(isBillableStop);
  const shown = customer.length ? customer : all;
  if (shown.length === 0) return { from: 'No stops yet', mid: '', to: '' };
  if (shown.length === 1) return { from: stopLabel(shown[0]), mid: '', to: '' };
  const n = shown.length - 2;
  return { from: stopLabel(shown[0]), mid: n > 0 ? ' → … +' + n + ' stop' + (n === 1 ? '' : 's') : '', to: stopLabel(shown[shown.length - 1]) };
};
// The two lines of a narrow card: the From, then '→ To' (or '… +N stops → To') -- the To is never cut off by a long From.
export const faceLines = (load) => {
  const f = faceParts(load);
  if (!f.to) return [f.from];
  return [f.from, (f.mid ? f.mid.replace(/^ → /, '') + ' → ' : '→ ') + f.to];
};
export const faceRoute = (load) => {
  const f = faceParts(load);
  return f.from + f.mid + (f.to ? ' → ' + f.to : '');
};

// { customer, route, tooltip } -- the tooltip (hover) has every stop with its full address, and the load number.
export const cardFace = (load) => {
  const customer = customerLabel(load) || 'Unknown customer';
  const route = faceRoute(load);
  const num = load && load.load_number ? 'Load ' + load.load_number + '\n' : '';
  return { customer, route, parts: faceParts(load), tooltip: customer + '\n' + num + loadRouteSummary(load).tooltip };
};

// What the board's search box matches: the face text, every stop's address and name, and the load number.
export const faceSearchText = (load) => [
  customerLabel(load), load && load.load_number, load && load.commodity,
  ...((load && load.load_stops) || []).map((st) => (st.place_name || '') + ' ' + (st.address || '')),
].filter(Boolean).join(' ').toLowerCase();

// ---------------------------------------------------------------- selection
export const toggleInSelection = (selected, id) => {
  const next = new Set(selected || []);
  if (next.has(id)) next.delete(id); else next.add(id);
  return next;
};

// Keep a selection only to ids that still exist (after a refresh).
export const pruneSelection = (selected, existingIds) => {
  const have = new Set(existingIds || []);
  return new Set([...(selected || [])].filter((id) => have.has(id)));
};

// Drag of a selected tile carries the whole selection (in list order); of an
// unselected tile, just that tile.
export const dragGroup = (draggedId, selected, orderedIds) => {
  if (!selected || !selected.has(draggedId) || selected.size < 2) return [draggedId];
  return (orderedIds || []).filter((id) => selected.has(id));
};

// Apply a saved per-viewer order to a list of ids: known ids in saved order,
// new ones after, in their incoming order.
export const applySavedOrder = (ids, saved) => {
  const pos = new Map((saved || []).map((id, i) => [id, i]));
  const known = ids.filter((id) => pos.has(id)).sort((a, b) => pos.get(a) - pos.get(b));
  const fresh = ids.filter((id) => !pos.has(id));
  return [...known, ...fresh];
};

export const moveItem = (list, fromIndex, toIndex) => {
  const out = list.slice();
  if (fromIndex < 0 || fromIndex >= out.length) return out;
  const [it] = out.splice(fromIndex, 1);
  out.splice(Math.max(0, Math.min(toIndex, out.length)), 0, it);
  return out;
};

// Insert ids into a draft's ordered list at index (ids already there are moved).
export const insertIntoOrder = (order, ids, index) => {
  const set = new Set(ids);
  const rest = (order || []).filter((id) => !set.has(id));
  const at = index == null ? rest.length : Math.max(0, Math.min(index, rest.length));
  return [...rest.slice(0, at), ...ids, ...rest.slice(at)];
};

// ---------------------------------------------------------------- stop order
// Stop ids for a draft: its saved interleaved order when it still covers exactly
// the draft's stops, else every job in order with its own stops in board order.
export const draftStopOrder = (loadsInOrder, savedStopOrder) => {
  const ids = [];
  (loadsInOrder || []).forEach((l) => orderedStops(l).forEach((s) => { if (s && s.id) ids.push(s.id); }));
  if (Array.isArray(savedStopOrder) && savedStopOrder.length === ids.length) {
    const want = new Set(ids);
    if (savedStopOrder.every((id) => want.has(id)) && new Set(savedStopOrder).size === ids.length) {
      return savedStopOrder.slice();
    }
  }
  return ids;
};

// Jobs in the order their first stop appears in a stop order.
export const loadOrderFromStopOrder = (stopOrder, stopToLoad) => {
  const seen = [];
  (stopOrder || []).forEach((sid) => { const l = stopToLoad[sid]; if (l && !seen.includes(l)) seen.push(l); });
  return seen;
};

// Every pickup of a job before every drop-off of the same job.
export const precedencePairs = (stops) => {
  const pairs = [];
  const byLoad = {};
  stops.forEach((s, i) => { (byLoad[s.loadId] = byLoad[s.loadId] || []).push(i); });
  Object.values(byLoad).forEach((idx) => {
    const p = idx.filter((i) => String(stops[i].stop_type || '').toLowerCase() === 'pickup');
    const d = idx.filter((i) => String(stops[i].stop_type || '').toLowerCase() === 'delivery');
    p.forEach((a) => d.forEach((b) => pairs.push([a, b])));
  });
  return pairs;
};

export const respectsPrecedence = (order, pairs) => {
  const pos = new Array(order.length);
  order.forEach((node, i) => { pos[node] = i; });
  return pairs.every(([a, b]) => pos[a] === undefined || pos[b] === undefined || pos[a] < pos[b]);
};

export const LATE_WEIGHT = 10;   // a minute late costs 10 minutes of driving
export const EARLY_WEIGHT = 1;   // a minute before the window opens costs 1

// Plan cost in minutes: day length (drive + time at each stop) plus lateness /
// earliness against each stop's time window. Mirrors the engine's ETA cascade:
// arrival = previous departure + drive; departure = arrival + stop time.
export const planCost = (order, m) => {
  let t = m.startTime || 0;
  let prev = -1;
  let penalty = 0;
  for (const i of order) {
    t += (prev < 0 ? (m.fromStart ? m.fromStart[i] || 0 : 0) : (m.dur[prev][i] || 0)) * 1000;
    const w = m.windows ? m.windows[i] : null;
    if (w) {
      if (w.end != null && t > w.end) penalty += ((t - w.end) / 60000) * LATE_WEIGHT;
      if (w.start != null && t < w.start) penalty += ((w.start - t) / 60000) * EARLY_WEIGHT;
    }
    t += (m.dwell[i] || 0) * 60000;
    prev = i;
  }
  return (t - (m.startTime || 0)) / 60000 + penalty;
};

// Best stop order under precedence. m = { n, dur[i][j] (s), fromStart[i] (s),
// dwell[i] (min), windows[i] {start,end ms}, startTime (ms), pairs }.
// Exact branch-and-bound up to `exactLimit` stops, else greedy + move-improve.
export const optimizeStopOrder = (m, exactLimit = 9) => {
  const n = m.n;
  if (n === 0) return { order: [], cost: 0, exact: true };
  const preds = Array.from({ length: n }, () => []);
  (m.pairs || []).forEach(([a, b]) => preds[b].push(a));
  const allowed = (i, used) => preds[i].every((p) => used[p]);

  if (n <= exactLimit) {
    let best = null;
    let bestCost = Infinity;
    const used = new Array(n).fill(false);
    const order = [];
    const dfs = () => {
      if (order.length === n) {
        const c = planCost(order, m);
        if (c < bestCost) { bestCost = c; best = order.slice(); }
        return;
      }
      if (order.length && planCost(order, m) >= bestCost) return;   // costs only grow
      for (let i = 0; i < n; i++) {
        if (used[i] || !allowed(i, used)) continue;
        used[i] = true; order.push(i);
        dfs();
        order.pop(); used[i] = false;
      }
    };
    dfs();
    return { order: best, cost: bestCost, exact: true };
  }

  // greedy: the allowed stop that adds the least cost next
  const used = new Array(n).fill(false);
  let order = [];
  for (let k = 0; k < n; k++) {
    let pick = -1;
    let pickCost = Infinity;
    for (let i = 0; i < n; i++) {
      if (used[i] || !allowed(i, used)) continue;
      const c = planCost([...order, i], m);
      if (c < pickCost) { pickCost = c; pick = i; }
    }
    if (pick < 0) { for (let i = 0; i < n; i++) if (!used[i]) { pick = i; break; } }   // cycle guard
    used[pick] = true; order.push(pick);
  }
  // improve: move one stop to another position while it helps
  let cost = planCost(order, m);
  for (let pass = 0; pass < 40; pass++) {
    let improved = false;
    for (let from = 0; from < n; from++) {
      for (let to = 0; to < n; to++) {
        if (to === from) continue;
        const cand = moveItem(order, from, to);
        if (!respectsPrecedence(cand, m.pairs || [])) continue;
        const c = planCost(cand, m);
        if (c + 1e-9 < cost) { order = cand; cost = c; improved = true; }
      }
    }
    if (!improved) break;
  }
  return { order, cost, exact: false };
};

// Straight-line drive estimate when Google is not available (same as the
// engine's simulated route: road distance = 1.3 x straight line, 50 km/h avg).
export const haversineKm = (a, b) => {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
};
export const estimateDriveSeconds = (a, b) => Math.round((haversineKm(a, b) * 1.3 / 50) * 3600);

// ---------------------------------------------------------------- finalize plan
// Same tiers as routingEngineService.assessReassignGuard (kept pure here so the
// test can run without the service; sim test checks they agree).
export const BLOCKED_STATUSES = ['complete', 'completed', 'ready_to_bill', 'billed', 'archived', 'cancelled'];
export const STARTED_STATUSES = ['en_route', 'at_pickup', 'loading', 'in_transit', 'at_delivery', 'unloading', 'pending_completion'];

// What finalizing does to each job. targetDriverId null = Unassigned.
//   stay        already this driver's (or already unassigned) - only the date / route order changes
//   ok          not on anyone / pending - moves freely
//   assigned    on another driver, not started - needs the confirm
//   inProgress  started (status or started_at) - needs the confirm, named loudly
//   blocked     finished - never moved
export const planFinalize = (liveLoads, targetDriverId, date) => {
  const out = { stay: [], ok: [], assigned: [], inProgress: [], blocked: [], dateChanges: [] };
  (liveLoads || []).forEach((l) => {
    const s = String(l.status || '').toLowerCase();
    const sameDriver = (l.driver_id || null) === (targetDriverId || null);
    if (BLOCKED_STATUSES.includes(s)) { out.blocked.push(l); return; }
    if (date && l.scheduled_date && String(l.scheduled_date).slice(0, 10) !== String(date).slice(0, 10)) out.dateChanges.push(l);
    if (sameDriver) { out.stay.push(l); return; }
    if (l.started_at || STARTED_STATUSES.includes(s)) { out.inProgress.push(l); return; }
    if (l.driver_id && s === 'assigned') { out.assigned.push(l); return; }
    out.ok.push(l);
  });
  out.needsConfirm = out.assigned.length + out.inProgress.length > 0;
  return out;
};

// The loads row change for one job on finalize.
export const finalizeLoadPatch = (load, targetDriverId, date) => {
  const s = String(load.status || '').toLowerCase();
  const patch = { driver_id: targetDriverId || null };
  if (date) patch.scheduled_date = String(date).slice(0, 10);
  if (targetDriverId) {
    if (s === '' || s === 'pending' || s === 'unassigned' || s === 'assigned') patch.status = 'assigned';
  } else if (s === 'assigned') {
    patch.status = 'pending';
  }
  return patch;
};

export const routeNumberFor = (date, driverId, draftId) => (driverId
  ? 'RT-' + date + '-' + String(driverId).slice(0, 4).toUpperCase()
  : 'RT-' + date + '-OPEN-' + String(draftId || Math.random().toString(36).slice(2)).slice(0, 4).toUpperCase());

export const todayLocalISO = (d = new Date()) => {
  const p = (x) => String(x).padStart(2, '0');
  return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
};
